import moment from "moment";

import { ConsumptionChartBy } from "types";
import { SensorDetails } from "types/Sensors";
import { apiFactory } from "generated";
import {
    createResponseStandard,
    createErrorResponseStandard,
} from "utils/functions";
import { TagData } from "generated/models";
import { ReportItemBuildQuery } from "types/Reports";
import { RateLimit } from "utils/rateLimit";

import AxiosInstance from "./axiosInstance";
import {
    getSingleConsumptionMetricDataV3,
    fetchMetricData,
    getTimezoneOffset,
} from "./proqueryService";

export class ReportService {
    private readonly tagClient = apiFactory.getTagsInstance(AxiosInstance);
    // Cap concurrent backend requests so the WebSocket service is not overwhelmed
    // when many users hit the report page at once. Shared across getSensorData
    // and getConsumptionMetricForReportPage (per-sensor calls).
    // dispatchIntervalMs spaces consecutive socket messages by 200ms to further
    // reduce backend stress when many sensors apply at once.
    private readonly requestLimit = new RateLimit(5, {
        batchMode: true,
    });
    private readonly queries: Record<ReportItemBuildQuery, string> = {
        room: 'mqtt2tsdb_digitalinput_na',
        waterLeakage: 'mqtt2tsdb_waterdetection_na',
        temperature: 'mqtt2tsdb_temperature_degree_c',
        humidity: 'mqtt2tsdb_humidity_percent',
        co2: 'mqtt2tsdb_co2_ppm',
        pressure: 'mqtt2tsdb_pressure_hpa',
        tvoc: 'mqtt2tsdb_voc_ppb',
        pm1: 'mqtt2tsdb_pm_ugm3',
        'pm2.5': 'mqtt2tsdb_pm_ugm3',
        pm10: 'mqtt2tsdb_pm_ugm3',
    };

    async getRoomsGroupsZones(orgId: string, value?: string, includeMembers?: boolean) {
        try {
            const res = await this.tagClient.getTags({
                organisation_id: orgId,
                value,
                with_relationship: includeMembers
            });
            return createResponseStandard<TagData[]>(res);
        } catch (error) {
            return createErrorResponseStandard<TagData[]>(error);
        }
    }

    /**
     * Make a query for a room or a sensor to get a month data. There is an exception for the Water leakage sensor.
     * Water leakage sensor has to get (daysInMonth + 1) days instead of days in a month
     * - Reason: there is an edge case of water leakage sensor.
     *   - The logic needs to check if there is a start pulse at the beginning of 1st day in a month
     * Room On time sensor has to get (daysInMonth + 1) days instead of days in a month
     * - Reason: there is an edge case of Room On time sensor.
     *   - The logic needs to know a previous status of the start day of the month
     * @param type The sensor type to build the query
     * @param gatewayId Gateway ID
     * @param ldsuUUID LDSU UID
     * @param said SAID number for the sensor index
     * @param daysInMonth The number of day in a month
     * @returns The query to get sensor data in a month
     */
    buildRoomAndSensorQuery(
        type: ReportItemBuildQuery,
        gatewayId: string,
        ldsuUUID: string,
        said: number,
        daysInMonth: number,
    ) {
        let edgeDayCheck = 0;

        if (type === 'waterLeakage' || type === 'room') {
            edgeDayCheck = 1;
        }

        const timezoneOffset = getTimezoneOffset();

        return `${this.queries[type]}{gateway="${gatewayId}", uid="${ldsuUUID}", said="${said}"}[${daysInMonth + edgeDayCheck}d]  offset ${timezoneOffset}h`;
    }

    async getSensorData(
        gatewayId: string,
        ldsuUUID: string,
        said: number,
        queryTime: number,
        type: ReportItemBuildQuery,
    ) {
        const month = moment.unix(queryTime);
        const wsQuery = this.buildRoomAndSensorQuery(type, gatewayId, ldsuUUID, said, month.daysInMonth());

        const params = {
            query: wsQuery,
            time: queryTime,
            originQuery: wsQuery,
        };

        const data = await this.requestLimit.run(() => fetchMetricData(params));

        return data.data.data.result;
    }

    async getConsumptionMetricForReportPage(
        sensorDetails: SensorDetails[],
        queryTime: number,
        groupBy: ConsumptionChartBy,
        metricType: 'waterflow' | 'energy',
    ) {
        try {
            const results = await this.requestLimit.map(sensorDetails, (detail) =>
                getSingleConsumptionMetricDataV3({
                    metricType,
                    sensorDetails: detail,
                    endTime: queryTime,
                    groupBy,
                }),
            );

            return {
                metricData: results.flatMap((result) => result.metricData),
                previousMetricData: results.flatMap((result) => result.previousMetricData),
            };
        } catch (error) {
            console.error('Failed to fetch consumption metrics for report', error);
            return { metricData: [], previousMetricData: [] };
        }
    }
}

const reportService = new ReportService();

export default reportService;
